import {IconName} from '../icons/paths';
import {Place, PlaceType} from '../types';

// Places arrive flat (parentId); the UI wants areas with their spots, in sort order.

export interface AreaGroup {
    area: Place;
    spots: Place[];
}

export function groupPlaces(items: Place[]): AreaGroup[] {
    const bySort = (a: Place, b: Place) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);
    const areas = items.filter(p => p.parentId === null).sort(bySort);
    const spotsByArea = new Map<string, Place[]>();
    for (const p of items) {
        if (p.parentId !== null) {
            const list = spotsByArea.get(p.parentId) ?? [];
            list.push(p);
            spotsByArea.set(p.parentId, list);
        }
    }
    return areas.map(area => ({area, spots: (spotsByArea.get(area.id) ?? []).sort(bySort)}));
}

/** "Bed 1 · Back garden" for a spot, just the name for an area. */
export function placeLabel(place: Place, all: Place[]): string {
    if (place.parentId === null) return place.name;
    const area = all.find(p => p.id === place.parentId);
    return area ? `${place.name} · ${area.name}` : place.name;
}

export const PLACE_TYPE_LABELS: Record<PlaceType, string> = {
    area: 'Area',
    bed: 'Bed',
    pot: 'Pot or container',
    greenhouse: 'Greenhouse or tunnel',
    indoors: 'Indoors',
    tree: 'Tree or bush',
    other: 'Somewhere else'
};

export const SPOT_TYPES: PlaceType[] = ['bed', 'pot', 'greenhouse', 'indoors', 'tree', 'other'];

export const PLACE_TYPE_ICONS: Record<PlaceType, IconName> = {
    area: 'map',
    bed: 'planted',
    pot: 'potted',
    greenhouse: 'leaf',
    indoors: 'journal',
    tree: 'tree',
    other: 'pin'
};

/** Moves an id one step within an ordered list; returns the same list when it cannot move. */
export function moveWithin(ids: string[], id: string, direction: -1 | 1): string[] {
    const from = ids.indexOf(id);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= ids.length) return ids;
    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, id);
    return next;
}
